import { useState } from 'react';

export default function FinalCTA() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.name || !formData.phone || !formData.service) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch('https://readdy.ai/api/form/d776ou55uau42b5in3ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimate-form" className="py-28 bg-stone-950 relative overflow-hidden">
      {/* Subtle dot grid */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{ backgroundImage: 'radial-gradient(circle, #d97706 1px, transparent 1px)', backgroundSize: '28px 28px' }}
      ></div>
      {/* Amber glow */}
      <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-amber-500/6 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left */}
          <div>
            <p className="text-amber-400 text-[11px] font-bold tracking-[0.25em] uppercase mb-5">Ready to Transform?</p>
            <h2 className="font-serif text-4xl md:text-5xl text-white mb-6 leading-tight">
              Schedule Your Service.<br />
              <span className="text-amber-400">Results Guaranteed.</span>
            </h2>
            <p className="text-stone-400 text-lg leading-relaxed mb-10">
              Join hundreds of Metro Atlanta homeowners who trust Carpet &amp; Flooring Transformers. Free estimates, transparent pricing, guaranteed results.
            </p>

            <ul className="space-y-4 mb-10">
              {[
                'Free, no-obligation estimates',
                '100% satisfaction guarantee — re-service free if needed',
                'Licensed, insured & certified technicians',
                'Financing available for qualifying projects',
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-stone-300 text-sm">
                  <div className="w-5 h-5 flex items-center justify-center text-amber-500 flex-shrink-0">
                    <i className="ri-check-double-line text-base"></i>
                  </div>
                  {item}
                </li>
              ))}
            </ul>

            {/* Phone CTA */}
            <a
              href="tel:7702361255"
              className="inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-stone-950 font-extrabold text-[15px] px-9 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill"></i>
              </div>
              Call 770.236.1255
            </a>

            <p className="text-stone-600 text-xs mt-5">Licensed &bull; Insured &bull; Certified &bull; 1943 Main St E, Snellville, GA 30078</p>

            {/* Social links */}
            <div className="flex gap-3 mt-8">
              {[
                { href: 'https://www.facebook.com/carpetandflooringtransformers/', icon: 'ri-facebook-fill', label: 'Facebook' },
                { href: 'https://www.instagram.com/flooringtransformers_/', icon: 'ri-instagram-line', label: 'Instagram' },
                { href: 'https://www.tiktok.com/@flooringtransformers', icon: 'ri-tiktok-fill', label: 'TikTok' },
                { href: 'https://www.youtube.com/channel/UCAVDmxqrkYrfISJyjl_T70g', icon: 'ri-youtube-fill', label: 'YouTube' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 flex items-center justify-center border border-stone-700 rounded-full text-stone-500 hover:text-amber-400 hover:border-amber-400 transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl p-8 lg:p-10">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-5">
                  <i className="ri-check-double-line text-3xl text-amber-600"></i>
                </div>
                <h3 className="font-bold text-stone-900 text-xl mb-2">Request Received!</h3>
                <p className="text-stone-500 text-sm leading-relaxed">
                  We&apos;ll contact you within 24 hours to confirm your free estimate. Or call us now at{' '}
                  <a href="tel:7702361255" className="text-amber-700 font-semibold">770.236.1255</a>.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-7">
                  <h3 className="font-bold text-stone-900 text-xl mb-1">Request a Free Estimate</h3>
                  <p className="text-stone-400 text-sm">We&apos;ll get back to you within 24 hours.</p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-5">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <div>
                    <label className="block text-[11px] font-bold text-stone-500 mb-1.5 uppercase tracking-widest">
                      Full Name <span className="text-amber-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your full name"
                      className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-amber-500 transition-colors"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-bold text-stone-500 mb-1.5 uppercase tracking-widest">
                        Phone <span className="text-amber-600">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(770) 000-0000"
                        className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-amber-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-stone-500 mb-1.5 uppercase tracking-widest">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="you@email.com"
                        className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-amber-500 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-stone-500 mb-1.5 uppercase tracking-widest">
                      Service Needed <span className="text-amber-600">*</span>
                    </label>
                    <select
                      name="service"
                      required
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-amber-500 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option value="Carpet Cleaning">Carpet Cleaning</option>
                      <option value="Carpet Repair">Carpet Repair</option>
                      <option value="LVP Installation">LVP Installation</option>
                      <option value="Hardwood Floor Services">Hardwood Floor Services</option>
                      <option value="Tile & Grout Cleaning">Tile &amp; Grout Cleaning</option>
                      <option value="Pet Odor & Stain Removal">Pet Odor &amp; Stain Removal</option>
                      <option value="Upholstery Cleaning">Upholstery Cleaning</option>
                      <option value="Air Duct Cleaning">Air Duct Cleaning</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-stone-500 mb-1.5 uppercase tracking-widest">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => { if (e.target.value.length <= 500) setFormData({ ...formData, message: e.target.value }); }}
                      placeholder="Tell us about your flooring needs..."
                      rows={3}
                      maxLength={500}
                      className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-amber-500 transition-colors resize-none"
                    />
                    <p className="text-xs text-stone-400 text-right mt-1">{formData.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-stone-900 hover:bg-amber-600 disabled:opacity-60 text-white font-extrabold py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    {submitting ? 'Sending...' : 'Request My Free Estimate →'}
                  </button>
                  <p className="text-center text-xs text-stone-400">No spam. No obligation. We respect your privacy.</p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
